/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { textOverflowEllipsis } from "@eisinc/App/SharedStyles/GeneralStyles";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button/Button";
import { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPrice from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@eisinc/Overrides/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable/Clickable";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface FeaturedProductItemStyles {
    container?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPriceWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productAvailabilityWrapper?: InjectableCss;
    addToCartContainer?: InjectableCss;
    uomGridItemStyle?: InjectableCss;
    qtyGridItem?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    chooseOptionsClickableStyles?: ClickablePresentationProps;
}

export const FeaturedProductItemStyles: FeaturedProductItemStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            background-color: white;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
            padding: 20px;
            margin-top: 28px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "20px",
            },
            css: css`
                margin-bottom: 5px;
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: "normal",
            lineHeight: "20px",
            css: css`
                margin-bottom: 5px;
            `,
        },
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                min-width: 100px;
            `,
        },
        image: {
            css: css`
                img {
                    height: 100%;
                }
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            * {
                font-size: 16px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
    productAvailabilityWrapper: {
        css: css`
            width: 100%;
        `,
    },
    addToCartContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    uomGridItemStyle: {
        css: css`
            width: 50px;
            max-width: 50px;
            flex-basis: 50px;
            height: 32px;
            margin-right: 10px;

            div {
                margin-top: 0 !important;
            }

            select {
                height: 32px !important;
                padding-right: 13px !important;
            }

            select + span {
                padding-right: 0 !important;
                width: 22px !important;
                right: 0 !important;
                top: 0 !important;
                height: 32px !important;
            }
        `,
    },
    qtyGridItem: {
        css: css`
            width: 65px;
            margin-right: 15px;

            @media screen and (max-width: 410px) {
                width: 40px;
                margin-right: 10px;
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;

                input {
                    text-align: center;
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        css: css`
            &.has-multiple-uom {
                width: calc(100% - 160px);

                @media screen and (max-width: 410px) {
                    width: calc(100% - 110px);
                }
            }

            &:not(.has-multiple-uom) {
                width: calc(100% - 80px);

                @media screen and (max-width: 410px) {
                    width: calc(100% - 50px);
                }
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    chooseOptionsClickableStyles: {
        css: css`
            display: block;
            width: 100%;
            font-size: 18px;
            letter-spacing: 0.5px;
            margin-bottom: 43px;

            &:focus {
                outline-width: 0;
            }

            > button {
                width: 100%;
            }
        `,
    },
    productImageWrapper: {
        css: css`
            width: 100px;
            height: 100px;
        `,
    },
};

const styles = FeaturedProductItemStyles;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, "navigation", ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type OwnProps = {
    product: ProductModel;
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const FeaturedProductItem: React.FC<Props> = ({ product, productInfo, settingsCollection, updateProductInfo }) => {
    if (!productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: "navigation", productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: "navigation", productId: product.id, unitOfMeasure });
    };

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const productContextAvailabilityExtendedStyles = {
        containerWithAdditionalMessage: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        separatorStyle: {
            css: css`
                display: none;
            `,
        },
        pickupMessage: {
            css: css`
                max-width: 165px;
                ${textOverflowEllipsis};
            `,
        },
        messageText: {
            css: css`
                max-width: 148px;
                ${textOverflowEllipsis};
            `,
        },
        availabilityByWarehouseLinkGridItem: {
            css: css`
                line-height: 1 !important;
            `,
        },
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper
                    css={css`
                        display: flex;
                        margin-bottom: 10px;
                    `}
                >
                    <StyledWrapper {...styles.productImageWrapper}>
                        {/* image */}
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            padding-left: 15px;
                        `}
                    >
                        {/* description */}
                        <ProductDescription
                            product={productContext}
                            {...({ showElipses: true } as any)}
                            {...({ showElipsesOn: "2" } as any)}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                        {/* price */}
                        {!product.isVariantParent && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label">{translate("U/M")}</VisuallyHidden>
                                <ProductPrice showLabel={false} product={productContext} />
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </StyledWrapper>
                        )}
                        {/* availability */}
                        {settingsCollection.productSettings.showInventoryAvailability && (
                            <StyledWrapper {...styles.productAvailabilityWrapper}>
                                <ProductContextAvailability extendedStyles={productContextAvailabilityExtendedStyles} />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                {/* actions */}
                {!product.isVariantParent ? (
                    <StyledWrapper {...styles.addToCartContainer}>
                        {(product?.unitOfMeasures as any)?.length > 1 && (
                            <StyledWrapper {...styles.uomGridItemStyle}>
                                <ProductUnitOfMeasureSelect labelOverride="" />
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.qtyGridItem}>
                            <VisuallyHidden as="label">{translate("QTY_quantity")}</VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                            />
                        </StyledWrapper>

                        <StyledWrapper
                            className={
                                (product?.unitOfMeasures as any)?.length > 1 ? "has-multiple-uom" : "no-multiple-uom"
                            }
                            {...styles.addToCartButtonGridItem}
                        >
                            <StyledWrapper>
                                <StyledWrapper
                                    css={css`
                                        min-height: 32px;
                                    `}
                                >
                                    <ProductAddToCartButton
                                        data-test-selector="addToCartBtn"
                                        extendedStyles={styles.productAddToCartButtonStyles}
                                        sizeVariant="large"
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.addToCartContainer}>
                        <Clickable href={productInfo.productDetailPath} {...styles.chooseOptionsClickableStyles}>
                            <Button variant="primary">
                                {translate("Choose from {0} Options").replace(
                                    "{0}",
                                    product.properties["styledChildrenCount"],
                                )}
                            </Button>
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProductItem);
